import { css } from '@emotion/react';
import { urls } from '@zapier/identity-utils';
import { atoms } from '@zapier/marketing-ui';

import { SocialOptionsSeparator } from '../global/SocialOptionsSeparator';
import { Invitation, useLogin } from '../../hooks';
import { QueryParams, RouteTo } from '../../types';
import { Captcha } from '../global/Captcha';
import { AltAuthCta } from './AltAuthCta';
import { EmailPasswordForm } from './EmailPasswordForm';
import { NewDeviceConfirmationPanel } from './NewDeviceConfirmationPanel';
import { NoAccountLink } from './NoAccountLink';
import { SocialItems } from './SocialItems';
import { WelcomeBackMessage } from './WelcomeBackMessage';

export type LoginFormProps = {
  /**
   * Autofocus the email field when rendering the login form
   */
  enableEmailAutoFocus?: boolean;
  /**
   * Key used by Google reCaptcha
   */
  googleCaptchaSiteKey: string;
  /**
   * Will ignore foreversession previous login when true
   */
  forceLoginDisplayForm?: boolean;
  /**
   * Forces users to log in to email account associated with invitation
   */
  invitation?: Invitation | null;
  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string | undefined;
  /**
   * Current query params (so they can be preserved when routing)
   */
  queryParams?: QueryParams;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: RouteTo;
  /**
   * Show the signup link (default to true)
   */
  showSignupLink?: boolean;

  /**
   * Should we render Google SSO?
   */
  googleSSOEnabled?: boolean;

  /**
   * Should we render Facebook SSO?
   */
  facebookSSOEnabled?: boolean;

  /**
   * Should we render Microsoft SSO?
   */
  microsoftSSOEnabled?: boolean;

  /**
   * Should we render Continue with SSO?
   */
  SAMLSSOEnabled?: boolean;
};

const rootStyle = css`
  display: grid;
  grid-gap: 20px;
`;

/**
 * Displays form to allow the user to log in. Supports username / password, SSO, SAML
 */
export function LoginForm({
  enableEmailAutoFocus = false,
  forceLoginDisplayForm = false,
  googleCaptchaSiteKey,
  invitation,
  next = urls.DEFAULT_LOGIN_NEXT,
  queryParams = {},
  routeTo,
  showSignupLink = true,
  googleSSOEnabled = true,
  microsoftSSOEnabled = true,
  facebookSSOEnabled = true,
  SAMLSSOEnabled = true,
}: LoginFormProps) {
  const {
    canSubmit,
    formData,
    formErrors,
    handleChange,
    handleClearForm,
    handleSubmit,
    isCaptchaRequired,
    newDeviceCodeStep,
    previousAuthType,
    shouldShowEmailPasswordForm,
    shouldShowEmailField,
    shouldShowFacebookSSO,
    shouldShowGoogleSSO,
    shouldShowMicrosoftSSO,
    shouldShowSAMLSSO,
    shouldShowPasswordField,
    status,
    submittedFields,
  } = useLogin({
    email: invitation?.email,
    defaultEmail: (queryParams?.email as string) || '',
    forceLoginDisplayForm,
    next,
    routeTo,
    googleSSOEnabled,
    microsoftSSOEnabled,
    facebookSSOEnabled,
    SAMLSSOEnabled: SAMLSSOEnabled,
  });

  const showSSOButtons = [
    googleSSOEnabled,
    microsoftSSOEnabled,
    facebookSSOEnabled,
    SAMLSSOEnabled,
  ].find(enabled => enabled === true || enabled === undefined);

  const hasSocial =
    shouldShowFacebookSSO ||
    shouldShowGoogleSSO ||
    shouldShowMicrosoftSSO ||
    shouldShowSAMLSSO;

  const isWelcomeBackShown =
    (!forceLoginDisplayForm || submittedFields.email) &&
    (!shouldShowEmailField || previousAuthType);
  const isSeparatorShown =
    shouldShowEmailPasswordForm &&
    shouldShowEmailField &&
    hasSocial &&
    showSSOButtons;
  const isAltAuthCtaShown =
    (!forceLoginDisplayForm || submittedFields.email) &&
    previousAuthType &&
    shouldShowEmailField;

  // Only show Captcha if it's required
  if (isCaptchaRequired) {
    return (
      <Captcha
        googleCaptchaSiteKey={googleCaptchaSiteKey}
        onCaptchaVerification={(captcha: string | null) => {
          handleChange('captcha', captcha || '');
        }}
      />
    );
  }

  // The password was correct, but this browser needs the emailed code first
  if (newDeviceCodeStep.challenge) {
    return (
      <section css={rootStyle} data-testid="new-device-confirmation">
        <NewDeviceConfirmationPanel
          challenge={newDeviceCodeStep.challenge}
          email={formData.email}
          googleCaptchaSiteKey={googleCaptchaSiteKey}
          step={newDeviceCodeStep}
        />
      </section>
    );
  }

  return (
    <section css={rootStyle}>
      {isWelcomeBackShown && (
        <WelcomeBackMessage
          email={previousAuthType && shouldShowEmailField ? '' : formData.email}
          onClear={handleClearForm}
        />
      )}
      {hasSocial && shouldShowEmailField && showSSOButtons && (
        <SocialItems
          next={next}
          selectedApis={queryParams?.selectedApis || []}
          shouldShowFacebookSSO={shouldShowFacebookSSO}
          shouldShowGoogleSSO={shouldShowGoogleSSO}
          shouldShowMicrosoftSSO={shouldShowMicrosoftSSO}
          shouldShowSAMLSSO={shouldShowSAMLSSO}
          queryParams={queryParams}
        />
      )}
      {isSeparatorShown && <SocialOptionsSeparator />}
      {shouldShowEmailPasswordForm && (
        <EmailPasswordForm
          canSubmit={canSubmit}
          formData={formData}
          formErrors={formErrors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          shouldDisableEmailField={Boolean(invitation?.email)}
          shouldFocusEmailField={Boolean(enableEmailAutoFocus)}
          shouldShowEmailField={shouldShowEmailField}
          shouldShowPasswordField={shouldShowPasswordField}
          status={status}
          submittedFields={submittedFields}
        />
      )}
      {formErrors.error && status === 'error' && (
        <div
          className={`${atoms.bodySM} ${atoms.textError} ${atoms.textCenter}`}
        >
          {formErrors.error}
        </div>
      )}
      {isAltAuthCtaShown && previousAuthType && showSSOButtons && (
        <AltAuthCta authType={previousAuthType} />
      )}
      {showSignupLink && <NoAccountLink queryParams={queryParams} />}
    </section>
  );
}
